import * as React from 'react';
import { labelOf, toneOf, type Tone } from '@mail-sv/shared';
import { Badge, StatusDot, type BadgeProps } from './badge.js';
import { cn } from '../lib/utils.js';

/**
 * 状態値をバッジで表示する。
 *
 * 色（`toneOf`）と日本語（`labelOf`）はどちらも `@mail-sv/shared` が唯一の出典。
 * **画面側で色や訳語を自前で持たない**こと。admin と portal で同じ状態が
 * 別の色・別の訳語で出ると、運営と契約者の会話が噛み合わなくなる。
 */

/** shared の `Tone` → Badge の variant。両者の語彙を 1 か所で繋ぐ。 */
const TONE_TO_VARIANT: Record<Tone, NonNullable<BadgeProps['variant']>> = {
  success: 'success',
  warning: 'warning',
  destructive: 'destructive',
  info: 'info',
  muted: 'muted',
  default: 'secondary',
};

export interface StatusBadgeProps extends Omit<BadgeProps, 'variant' | 'children'> {
  /** enum の生値（`active` / `over_quota` / `unreachable` …）。 */
  status: string | null | undefined;
  /** 先頭に状態ドットを出す。テーブル行の可読性が上がる。 */
  dot?: boolean;
  /** ドットを点滅させる（`applying` など進行中の状態向け）。 */
  pulse?: boolean;
  /** 訳語を上書きしたいときだけ渡す。通常は `labelOf` に任せる。 */
  label?: React.ReactNode;
  /** トーンを上書きしたいときだけ渡す。 */
  tone?: Tone;
}

export function StatusBadge({
  status,
  dot = false,
  pulse = false,
  label,
  tone,
  size,
  className,
  ...props
}: StatusBadgeProps) {
  const resolvedTone = tone ?? toneOf(status);
  const variant = TONE_TO_VARIANT[resolvedTone];
  // `default` トーンだけはドットの色語彙に無いので muted に寄せる。
  const dotTone = resolvedTone === 'default' ? 'muted' : resolvedTone;

  return (
    <Badge
      variant={variant}
      size={size}
      className={cn(className)}
      title={typeof status === 'string' ? status : undefined}
      {...props}
    >
      {dot ? <StatusDot tone={dotTone} pulse={pulse} /> : null}
      {label ?? labelOf(status)}
    </Badge>
  );
}

/**
 * ノードの `displayStatus` 専用（§4.3）。
 * `unreachable` と `degraded` は運用アラート対象なのでドットを常に出し、
 * `provisioning` は進行中であることが伝わるよう点滅させる。
 */
export function NodeStatusBadge({
  displayStatus,
  className,
  ...props
}: Omit<StatusBadgeProps, 'status' | 'dot' | 'pulse'> & { displayStatus: string }) {
  return (
    <StatusBadge
      status={displayStatus}
      dot
      pulse={displayStatus === 'provisioning'}
      className={className}
      {...props}
    />
  );
}
